import { SDKPageData } from "../_types";

export const JSON_SDK_MAJIK_SIGNATURE: SDKPageData = {
  id: "majik-signature",
  timestamp: new Date("2026-02-11").toISOString(),
  lastUpdate: new Date("2026-08-04").toISOString(),
  version: "0.2.0",
  metadata: {
    sdkName: "@majikah/majik-signature",
    sdkSlug: "majik-signature",
    tagline: "Hybrid post-quantum content signing and verification",
    description:
      "A hybrid post-quantum content signing and verification library for the Majikah ecosystem. Combines classical Ed25519 with post-quantum ML-DSA-87 (FIPS-204) to provide tamper-evident, forgery-resistant digital signatures for any content format. Supports multi-party signing with allowlists, envelope sealing, and chronological signing-order verification; optional trusted timestamps (TSA); detached signing via a self-describing binary envelope format (.mjksig); batch signing of entire folders or archives against a single manifest (.mjksmap); blockchain anchor registration for sealed envelopes; and built-in file embedding for PDFs, images, audio, video, documents, and more.",
    seo: "majik-signature post-quantum digital signatures Ed25519 ML-DSA-87 FIPS-204 content signing file embedding multi-signature allowlist sealing trusted timestamp detached signing batch signing signing order verification chain anchoring PDF signing audio video cryptography JavaScript TypeScript",
    keywords: [
      "digital-signatures",
      "post-quantum",
      "ml-dsa-87",
      "ed25519",
      "content-signing",
      "file-embedding",
      "multi-signature",
      "signing-allowlist",
      "envelope-sealing",
      "trusted-timestamp",
      "detached-signing",
      "signature-envelope",
      "batch-signing",
      "signing-order",
      "mjksig",
      "mjksmap",
      "chain-anchoring",
      "blockchain-anchor",
      "pdf-signing",
      "audio-signing",
      "video-signing",
      "cryptography",
      "majikah",
      "hybrid-crypto",
      "fips-204",
      "tamper-evident",
      "verification",
    ],
    category: "Cryptography",
    subcategory: "Digital Signatures & Content Authentication",
    language: "TypeScript",
    platforms: [
      "Node.js",
      "Browser",
      "Electron",
      "Tauri",
      "Deno",
      "Bun",
      "Cloudflare Workers",
    ],
    photo: {
      logo: "https://storage.majikah.solutions/public/Logo_MajikSignature_Majikah_Thumbnail_WEBP.webp?url",
      banner:
        "https://storage.majikah.solutions/public/MajikSignature_SmallTile.jpg?url",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikSignature_SocialCard.webp?url",
    },
    badges: [
      {
        label: "Developed by",
        value: "Majikah Solutions OPC",
        type: "developed-by",
        url: "https://github.com/Majikah",
      },
      {
        label: "Version",
        value: "0.2.0",
        type: "version",
      },
      {
        label: "Downloads",
        value: "",
        type: "downloads",
      },
      {
        label: "Bundle Size",
        value: "",
        type: "bundle-size",
      },
      {
        label: "License",
        value: "Apache-2.0",
        type: "license",
        url: "https://opensource.org/licenses/Apache-2.0",
      },
      {
        label: "TypeScript",
        value: "Ready",
        type: "typescript",
      },
      {
        label: "Stars",
        value: "",
        type: "stars",
      },
      {
        label: "Language",
        value: "TypeScript",
        type: "language",
      },
      {
        label: "",
        value: "jedlsf",
        type: "sponsors",
      },
      {
        label: "Security",
        value: "Post-Quantum",
        type: "custom",
      },
      {
        label: "Signing",
        value: "Multi-Party",
        type: "custom",
      },
      {
        label: "Batch",
        value: "Manifest-Based",
        type: "custom",
      },
      {
        label: "Order",
        value: "Chronological Verification",
        type: "custom",
      },
    ],
  },
  repository: {
    url: "https://github.com/Majikah/majik-signature",
    owner: "Majikah",
    repo: "majik-signature",
    branch: "main",
    license: "Apache-2.0",
  },
  hero: {
    title: "Majik Signature",
    subtitle: "Hybrid Post-Quantum Content Signing",
    description:
      "Tamper-evident, forgery-resistant digital signatures for any content format. Combines classical Ed25519 with post-quantum ML-DSA-87 (FIPS-204) for dual-algorithm security. Sign raw bytes, JSON payloads, or embed signatures directly into PDFs, images, audio, video, and documents — with multi-party signing, allowlists, sealing, chronological order verification, detached envelopes, batch signing, and trusted timestamps built in.",
    codeSnippet: {
      language: "typescript",
      code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikSignature } from '@majikah/majik-signature';\n\n// Sign any content\nconst key = await MajikKey.create(mnemonic, 'passphrase', 'My Key');\nconst signature = await MajikSignature.sign(content, key);\n\n// Verify (no private key needed)\nconst result = MajikSignature.verifyWithKey(content, signature, key);\nconsole.log('Valid:', result.valid);        // true\nconsole.log('Signed by:', result.signerId); // fingerprint\n\n// Sign and embed into a file\nconst { blob: signedFile } = await MajikSignature.signFile(pdfBlob, key);\n// signedFile is a valid PDF with the signature embedded",
      caption:
        "Sign content or files with hybrid post-quantum security in three lines.",
    },
    ctaButtons: [
      {
        label: "Install via npm",
        variant: "primary",
        url: "https://www.npmjs.com/package/@majikah/majik-signature",
        external: true,
      },
      {
        label: "View on GitHub",
        variant: "secondary",
        url: "https://github.com/Majikah/majik-signature",
        external: true,
      },
    ],
  },
  overview: {
    title: "What is Majik Signature?",
    paragraphs: [
      "Majik Signature is a hybrid post-quantum digital signature library built on top of Majik Key. It provides cryptographic proof that a specific piece of content was produced or approved by the holder of a specific Majik Key account, that the content has not been modified since it was signed, and that the signature remains valid against future quantum computing threats.",
      "Every signature is produced by two independent algorithms over the same canonical, domain-separated payload: Ed25519 (classical, 128-bit security) and ML-DSA-87 (post-quantum, FIPS-204, Category 5 security). Verification requires both to pass, meaning no single algorithmic break is sufficient to forge a valid signature.",
      "Files don't just carry a single signature — they carry a multi-signature envelope, modeled by the immutable MajikSignatureEnvelope class. Any number of independent parties can sign the same file, signing can optionally be restricted to a pre-defined allowlist of signers, the issuer of that allowlist can permanently seal the envelope once signing is complete, and the chronological order in which parties signed can be independently verified against an expected sequence.",
      "Signatures don't have to live embedded in a file at all — signFileDetached() produces a portable envelope that travels separately from the file, serializable as JSON, base64, or a dedicated self-describing binary container (.mjksig). An entire batch of files (a folder, a zip's contents) can be signed against a single manifest (.mjksmap) in one call, with relocation-tolerant batch verification that still finds and verifies a file even if it was renamed or moved after signing.",
      "The library supports signing raw bytes, strings, and JSON payloads, with built-in file embedding that works across PDFs, images, audio, video, documents, and more. Signatures are embedded directly into each format's native container whenever possible — no sidecar files needed — with a universal binary-trailer fallback for any format without a dedicated handler.",
      "Sealed envelopes can additionally have a blockchain anchor registered against them — the library builds the canonical memo to submit on-chain and embeds an already-confirmed anchor back into the envelope, while transaction submission and confirmation remain the responsibility of an external chain-integration layer.",
      "Verification is fully public — anyone with the signer's public keys can verify. No private key is ever needed for verification, making it safe to verify content in any context.",
    ],
    highlights: [
      {
        icon: "shield",
        label: "Dual-Algorithm Security",
        description:
          "Ed25519 + ML-DSA-87 (FIPS-204). Both must verify — a break in one algorithm is not sufficient to forge a signature.",
      },
      {
        icon: "users",
        label: "Multi-Party Signing",
        description:
          "Any number of independent signers per file, with cryptographically committed allowlists, issuer-controlled sealing, and chronological order verification.",
      },
      {
        icon: "file",
        label: "Native File Embedding",
        description:
          "Sign and embed into PDFs, PNGs, WAVs, MP4s, DOCXs, and more. Signature lives inside the file — no sidecar files.",
      },
      {
        icon: "layers",
        label: "Detached & Batch Signing",
        description:
          "Produce portable envelopes independent of the file, or sign an entire folder against one manifest — both with a self-describing binary container format.",
      },
      {
        icon: "clock",
        label: "Trusted Timestamps",
        description:
          "Optional TSA-signed timestamps bind a digest to a server-authoritative time, itself protected by the same hybrid signature scheme — and preferred automatically for order verification.",
      },
      {
        icon: "link",
        label: "Chain Anchoring",
        description:
          "Register a confirmed blockchain anchor against a sealed envelope's hash commitment, for independent, external notarization.",
      },
    ],
    whatItDoes: [
      "Signs raw bytes, UTF-8 strings, and JSON payloads with hybrid Ed25519 + ML-DSA-87",
      "Embeds signatures directly into file containers (PDF, PNG, WAV, MP3, MP4/MOV, DOCX/XLSX/PPTX/ODF, MKV/WebM, text formats, and more)",
      "Produces detached signature envelopes independent of file embedding — portable as JSON, base64, or a self-describing .mjksig binary container",
      "Signs entire batches of files (folders, zip contents) against a single .mjksmap manifest in one call, packaged as a manifest or as separate per-file envelopes",
      "Resolves batch-verified files by content hash when a path lookup misses, tolerating files renamed or relocated after signing",
      "Supports multi-signature envelopes — any number of independent signers on one file",
      "Enforces signing allowlists, cryptographically committed to the issuer's own signature",
      "Seals envelopes to permanently lock out further signing, via a SHA3-512 hash commitment over all signatories",
      "Verifies chronological signing order against an expected sequence of signers, preferring TSA-attested timestamps over self-reported ones, with an optional strict mode",
      "Attaches trusted timestamps (TSA) — a server-signed binding of digest, nonce, and authoritative time",
      "Registers confirmed blockchain anchors against sealed envelopes and builds the canonical memo to submit on-chain",
      "Verifies signatures against content and signer's public keys",
      "Extracts every embedded signature from a file as typed instances",
      "Strips embedded signatures to recover the exact original bytes",
      "Detects tampering — any byte change invalidates both signatures",
      "Binds signer identity (fingerprint), timestamp, and content hash into the signed payload",
      "Serializes signatures and envelopes to JSON, base64, or a versioned binary container for storage and transport",
      "Auto-detects file formats via magic bytes and MIME types",
      "Provides idempotent re-signing (strips any existing envelope before signing)",
    ],
    whatItDoesNot: [
      "Does not manage cryptographic keys — use @majikah/majik-key for that",
      "Does not encrypt content — signatures prove integrity and authenticity, not confidentiality",
      "Does not verify signer identity in the real world — only that content was signed by a specific key",
      "Does not enforce a validity window on timestamps — a legitimately old signature still verifies as valid",
      "Does not guarantee Tier-2 (universal trailer) signatures survive file re-encoding or re-muxing for formats without a dedicated handler",
      "Does not provide a revocation mechanism — a compromised key's past signatures remain cryptographically valid",
      "Does not submit or confirm blockchain transactions itself — chain anchoring only builds the memo and registers an already-confirmed anchor, submission and confirmation are external",
      "Does not treat a self-reported (non-TSA) signature timestamp as independently attested proof of order — order-verification results flag when they relied on one",
    ],
    targetUsers: [
      {
        title: "Content Creators & Publishers",
        description:
          "Prove authorship of music, art, videos, documents, or datasets. Embed signatures directly in files for distribution.",
        icon: "user",
      },
      {
        title: "Multi-Party Document Workflows",
        description:
          "Contracts, invoices, and records that need independent sign-off from more than one party, in a required order, with a final seal once everyone has signed.",
        icon: "users",
      },
      {
        title: "Approval Workflow Builders",
        description:
          "Applications that need to prove not just who signed, but that they signed in the required sequence — e.g. requester before approver, legal before finance.",
        icon: "check-circle",
      },
      {
        title: "Majikah App Developers",
        description:
          "Add content provenance to apps built on Majik Message, Majik Buwiz, or Majik Universal ID. Sign API payloads, files, or messages with user identities.",
        icon: "code",
      },
      {
        title: "Security-First Developers",
        description:
          "Applications requiring post-quantum signature security today, with tamper detection, non-repudiation, optional trusted timestamping, and external notarization via chain anchoring.",
        icon: "shield",
      },
      {
        title: "Batch & Archival Pipelines",
        description:
          "Sign or verify every asset in a folder, project export, or zip archive against a single manifest, resilient to files being reorganized or renamed afterward.",
        icon: "folder",
      },
    ],
  },
  installation: {
    title: "Installation",
    packageManagers: [
      {
        name: "npm",
        command: "npm install @majikah/majik-signature @majikah/majik-key",
      },
      {
        name: "yarn",
        command: "yarn add @majikah/majik-signature @majikah/majik-key",
      },
      {
        name: "pnpm",
        command: "pnpm add @majikah/majik-signature @majikah/majik-key",
      },
      {
        name: "bun",
        command: "bun add @majikah/majik-signature @majikah/majik-key",
      },
    ],
    verification: [
      {
        description:
          "Verify the install by signing a simple string in Node.js or your browser console:",
        code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikSignature } from '@majikah/majik-signature';\n\nconst mnemonic = MajikKey.generateMnemonic();\nconst key = await MajikKey.create(mnemonic, 'test', 'Test');\nconst sig = await MajikSignature.sign('Hello, World!', key);\nconsole.log('Signature created:', sig.signerId);",
        expectedOutput: "Signature created: [base64-fingerprint]",
      },
    ],
  },
  quickStart: {
    title: "Quick Start",
    description:
      "The core workflows: signing content, verifying signatures, signing and verifying files, multi-party signing with allowlists and order verification, sealing, detached envelopes, and batch signing.",
    steps: [
      {
        title: "1. Sign raw content",
        description:
          "Sign any byte array or UTF-8 string with an unlocked MajikKey. Both Ed25519 and ML-DSA-87 signatures are computed over a canonical payload that binds the signer ID, timestamp, and SHA-256 content hash.",
        code: {
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikSignature, CONTENT_TYPES } from '@majikah/majik-signature';\n\n// Create and unlock a key\nconst mnemonic = MajikKey.generateMnemonic();\nconst key = await MajikKey.create(mnemonic, 'passphrase', 'My Key');\n\n// Sign content\nconst document = 'This is the original content.';\nconst signature = await MajikSignature.sign(document, key, {\n  contentType: CONTENT_TYPES.TEXT,\n});\n\nconsole.log('Signed by:', signature.signerId);\nconsole.log('At:', signature.timestamp);\nconsole.log('Content hash:', signature.contentHash);",
        },
        notes: [
          "The key must be unlocked — call key.unlock(passphrase) if loading from storage.",
          "contentType is advisory only and not cryptographically enforced.",
          "Strings are UTF-8 encoded before hashing.",
        ],
      },
      {
        title: "2. Verify a signature",
        description:
          "Verification requires only the signer's public keys — no private key needed. Both Ed25519 and ML-DSA-87 must verify for the result to be valid.",
        code: {
          language: "typescript",
          code: "// Verify with public keys\nconst publicKeys = MajikSignature.publicKeysFromMajikKey(key);\nconst result = MajikSignature.verify(document, signature, publicKeys);\n\nconsole.log('Valid:', result.valid);      // true\nconsole.log('Signer:', result.signerId);  // fingerprint\n\n// Shorthand: verify directly against a MajikKey (works on locked keys)\nconst result2 = MajikSignature.verifyWithKey(document, signature, key);\nconsole.log('Valid:', result2.valid);     // true\n\n// Tamper detection\nconst tampered = document + ' (modified)';\nconst tamperResult = MajikSignature.verifyWithKey(tampered, signature, key);\nconsole.log('Tampered:', tamperResult.valid); // false",
        },
        notes: [
          "If result.valid is false, check result.reason for a human-readable explanation.",
          "Always check result.signerId matches an expected fingerprint in your application.",
        ],
      },
      {
        title: "3. Sign and embed into a file",
        description:
          "Sign a file and embed the signature directly into its container in one call. The file format is auto-detected, and the signature is stored using the appropriate format-specific handler. PDFs stay PDFs, WAVs stay WAVs.",
        code: {
          language: "typescript",
          code: '// Sign and embed in one call\nconst { blob: signedFile, handler } = await MajikSignature.signFile(file, key);\n\nconsole.log(\'Signed using handler:\', handler);\n// e.g., "PDF", "WAV", "MP4/MOV", "PNG", "Office (DOCX/XLSX/PPTX/ODF)"\n\n// The signed file is the same format as the input — ready to save or upload\nawait uploadToServer(signedFile);',
        },
        notes: [
          "signFile() strips any existing envelope before signing, so re-signing is always safe.",
          "The signature covers the original file bytes before embedding.",
          "Pass expectedSigners in options to establish a signing allowlist on the first signature.",
        ],
      },
      {
        title: "4. Verify an embedded signature",
        description:
          "Verify every signature embedded in a file. A file may carry more than one signature, so verifyFile() always returns an array — one VerificationResult per signer.",
        code: {
          language: "typescript",
          code: "// Verify embedded signatures (key can be locked)\nconst results = await MajikSignature.verifyFile(signedFile, key);\n\nconst allValid = results.every((r) => r.valid);\nconsole.log('All signatures valid:', allValid);\nfor (const r of results) {\n  console.log(r.signerId, r.valid, r.timestamp);\n}\n\n// Check if a file is signed (fast guard, no verification)\nconst isSigned = await MajikSignature.isSigned(file);\nif (isSigned) {\n  // Then verify...\n}",
        },
      },
      {
        title: "5. Multi-party signing with an allowlist",
        description:
          "Restrict who may sign a file, then track signing status as each party adds their signature.",
        code: {
          language: "typescript",
          code: "// First signer establishes the allowlist\nconst { blob: step1 } = await MajikSignature.signFile(file, issuerKey, {\n  expectedSigners: [\n    MajikSignature.expectedSignerFromKey(issuerKey),\n    MajikSignature.expectedSignerFromKey(counterpartyKey),\n  ],\n});\n\n// Counterparty signs — permitted, they're on the allowlist\nconst { blob: step2 } = await MajikSignature.signFile(step1, counterpartyKey);\n\n// Check status\nconst { signed, pending } = await MajikSignature.getSignatories(step2);\nconsole.log('Pending:', pending.map((s) => s.signerId));\n\n// Issuer seals once everyone required has signed\nconst { blob: sealed } = await MajikSignature.seal(step2, issuerKey);",
        },
        notes: [
          "expectedSigners is only honored on the first signature — later calls ignore it, the existing allowlist always wins.",
          "A non-listed signer is rejected with MajikSignatureAllowlistError before any cryptographic operation runs.",
          "Only the issuer (the signer who established the allowlist) can seal, and a sealed envelope rejects all further signing.",
        ],
      },
      {
        title: "6. Verify signing order",
        description:
          "Beyond confirming who signed, confirm they signed in a required sequence. TSA-attested timestamps are preferred automatically; self-reported timestamps are used as a fallback and flagged.",
        code: {
          language: "typescript",
          code: "const orderResult = await MajikSignature.verifyFileOrder(sealed, [\n  issuerKey,\n  counterpartyKey,\n]);\n\nif (orderResult.valid) {\n  console.log('Signed in the expected order, all valid.');\n} else {\n  console.log('Reason:', orderResult.reason);\n  console.log('Violations:', orderResult.violations);\n}\n\nif (orderResult.usesUnattestedTimestamp) {\n  console.log('Note: relied on at least one self-reported (non-TSA) timestamp.');\n}",
        },
        notes: [
          "expectedOrder accepts MajikKey instances and/or ExpectedSigner objects, mixed freely.",
          "Pass { strict: true } to also fail if anyone outside expectedOrder signed at all.",
        ],
      },
      {
        title: "7. Sign detached, and sign a batch",
        description:
          "Produce a signature envelope that travels independently of the file, or sign an entire batch of files against a single manifest.",
        code: {
          language: "typescript",
          code: "// Detached — envelope travels separately from the file\nconst { blob, envelope } = await MajikSignature.signFileDetached(file, key);\nconst mjksigBlob = envelope.toMJKSIG(); // portable binary container\n\n// Batch — sign every file in a folder against one manifest\nconst result = await MajikSignature.signBatchDetached(\n  [\n    { path: 'docs/report.pdf', blob: reportBlob },\n    { path: 'docs/appendix.pdf', blob: appendixBlob },\n  ],\n  key,\n);\n\nif (result.mode === 'map') {\n  zip.file('signatures.mjksmap', await result.mapBlob.arrayBuffer());\n}",
        },
        notes: [
          "verifyFileDetached() always strips the file first, so a stray embedded envelope never interferes.",
          "signBatchDetached() defaults to mode: 'map' — pass mode: 'separate' for one .mjksig per file instead.",
        ],
      },
    ],
  },
  features: [
    {
      id: "hybrid-dual-algorithm",
      title: "Hybrid Dual-Algorithm Signing",
      description:
        "Every signature is produced by two independent algorithms: Ed25519 (classical) and ML-DSA-87 (post-quantum, FIPS-204). Both must verify for the signature to be valid, providing defense-in-depth against algorithmic breaks.",
      icon: "shield",
      content: [
        "Ed25519 provides 128-bit classical security with a 64-byte signature.",
        "ML-DSA-87 (NIST FIPS-204, Category 5) provides post-quantum security with a ~4595-byte signature.",
        "Both algorithms sign the same canonical payload: a domain-separated string binding version, signer ID, timestamp, content type, and SHA-256 content hash.",
        "A classical attacker breaking Ed25519 cannot forge the ML-DSA-87 signature. A quantum attacker breaking ML-DSA-87 cannot forge the Ed25519 signature.",
        "No single algorithmic break is sufficient to produce a valid forgery.",
      ],
      table: {
        headers: [
          "Algorithm",
          "Type",
          "Security Level",
          "Signature Size",
          "Public Key Size",
        ],
        rows: [
          {
            cells: [
              "Ed25519",
              "Classical (ECC)",
              "128-bit",
              "64 bytes",
              "32 bytes",
            ],
          },
          {
            cells: [
              "ML-DSA-87",
              "Post-Quantum (Lattice, FIPS-204)",
              "Category 5",
              "~4595 bytes",
              "2592 bytes",
            ],
          },
        ],
      },
      benefits: [
        "Downgrade attack resistance — both must be broken simultaneously",
        "Future-proof against quantum computing advances",
        "Provides classical and post-quantum security in a single envelope",
      ],
    },
    {
      id: "canonical-payload-binding",
      title: "Canonical Payload Binding",
      description:
        "Both signatures cover a domain-separated canonical payload that binds together the signer identity, timestamp, content type, and SHA-256 hash of the content. This binding prevents signature reuse, replay, and forgery.",
      icon: "lock",
      content: [
        'The canonical payload format is: "majik-signature-v1:" + JSON({ v, id, ts, ct, hash[, alh] })',
        "v — Envelope version (1)",
        "id — Signer fingerprint (SHA-256 of X25519 public key from MajikKey)",
        "ts — ISO 8601 timestamp",
        'ct — Content type (advisory, e.g., "audio/wav", "application/pdf", or null)',
        "hash — SHA-256 of the content, base64-encoded",
        "alh — SHA-256 of the canonical allowlist, base64-encoded — present only when this signer is establishing an allowlist",
        'The domain separator ("majik-signature-v1:") prevents cross-protocol signature reuse — a Majik Signature cannot be mistaken for a signature from another system or another Majik protocol.',
        "alh is omitted entirely (never set to null) on every signature that isn't establishing an allowlist — this preserves byte-identical payloads for every signature made before multi-sig support existed.",
        "Because the signer ID and timestamp are part of the signed payload, a valid signature cannot be transferred to a different signer or replayed with a modified timestamp.",
      ],
      codeExample: {
        title: "Canonical Payload Structure",
        language: "typescript",
        code: "// Example canonical payload (what gets signed)\nconst payload = `majik-signature-v1:${JSON.stringify({\n  v: 1,\n  id: 'base64-fingerprint-of-signer',\n  ts: '2026-01-01T00:00:00.000Z',\n  ct: 'audio/wav',\n  hash: 'base64-sha256-of-content'\n  // alh omitted — this signer is not establishing an allowlist\n})}`;\n\n// Both Ed25519 and ML-DSA-87 sign this exact string",
      },
    },
    {
      id: "multi-signature-allowlist",
      title: "Multi-Signature Envelopes & Allowlists",
      description:
        "Files carry a MultiSigEnvelope, not a single signature — any number of independent parties can sign the same file. Signing can optionally be restricted to a pre-defined allowlist, cryptographically committed to the issuer's own signature.",
      icon: "users",
      content: [
        "Open signing (default): any MajikKey may add a signature to a file.",
        "Restricted signing: the first signer supplies an expectedSigners allowlist via SignOptions. Later signFile() calls ignore expectedSigners — the existing allowlist always wins.",
        "The allowlist is enforced before any cryptographic operation — a non-listed signer's attempt is rejected immediately with MajikSignatureAllowlistError.",
        "Allowlist membership requires all three fields to match: signerId (fingerprint), edPublicKey, and mlDsaPublicKey — preventing a signer from spoofing membership with a different key that happens to share a fingerprint.",
        "The allowlist's SHA-256 hash (allowlistHash) is embedded in the issuer's own canonical signing payload — tampering with the allowlist after the fact invalidates the issuer's signature.",
        "canSign() lets an application check permission (and get a human-readable reason) before attempting to sign.",
        "getSignatories() / getIssuer() / getEnvelopeInfo() provide full signing-status introspection — who has signed, who is pending, and who established the allowlist — without manually walking the envelope.",
        "Old, pre-multi-sig files (a bare single-signature envelope) are transparently promoted to a one-item MultiSigEnvelope by the library — every public API always works with MultiSigEnvelope semantics.",
        "The entire model is exposed as an immutable MajikSignatureEnvelope class — every mutation (withSignature, withAllowlist, withSeal, withChainAnchor) returns a new instance rather than mutating in place.",
      ],
      codeExample: {
        title: "Restrict signing to specific parties",
        language: "typescript",
        code: "const { blob: step1 } = await MajikSignature.signFile(file, aliceKey, {\n  expectedSigners: [\n    MajikSignature.expectedSignerFromKey(aliceKey),\n    MajikSignature.expectedSignerFromKey(bobKey),\n  ],\n});\n\n// Bob is on the allowlist — this succeeds\nconst { blob: step2 } = await MajikSignature.signFile(step1, bobKey);\n\n// Eve is not — this throws MajikSignatureAllowlistError\nawait MajikSignature.signFile(step2, eveKey);",
      },
      benefits: [
        "Contracts, invoices, and multi-party documents get real independent signatures, not one signature 'on behalf of' several people",
        "Restricted signing is enforced by the library itself, not left to application logic",
        "Allowlist tampering is cryptographically detectable via the issuer's own signature",
      ],
    },
    {
      id: "envelope-sealing",
      title: "Envelope Sealing",
      description:
        "The issuer who established an allowlist can permanently seal the envelope once signing is complete, rejecting all further signing attempts — including from the issuer themself.",
      icon: "lock",
      content: [
        'Sealing computes a SHA3-512 hash over every current signatory plus a seal timestamp, domain-separated with "majik-seal-v1:".',
        "The seal hash is a hash commitment, not a new cryptographic signature — verifySeal() checks hash equality, it does not perform Ed25519/ML-DSA-87 verification.",
        "Only the issuer may seal (sealedBy must equal allowlistSignerId), and only once.",
        "A sealed envelope's sealHash, sealTimestamp, and sealedBy fields are stored alongside the signatures array in the file's MultiSigEnvelope.",
        "isSealed() / getSealInfo() provide fast structural checks without recomputing the hash.",
        "A sealed envelope is also the prerequisite for chain anchoring — canAnchor() checks this before a memo can be built.",
      ],
      codeExample: {
        title: "Seal a completed multi-party envelope",
        language: "typescript",
        code: "const { blob: sealed, sealInfo } = await MajikSignature.seal(step2, aliceKey);\nconsole.log('Sealed at:', sealInfo.sealTimestamp);\n\n// Anyone can verify the seal structurally\nconst sealResult = await MajikSignature.verifySeal(sealed);\nconsole.log('Seal valid:', sealResult.valid, 'by:', sealResult.sealedBy);\n\n// Verify the underlying signatures separately\nconst sigResults = await MajikSignature.verifyFile(sealed, aliceKey);",
      },
    },
    {
      id: "signature-order-verification",
      title: "Signing Order Verification",
      description:
        "Beyond confirming who signed, verify that a set of expected signers signed in a required chronological sequence — e.g. 'Bob must sign before Dave.' TSA-attested timestamps are preferred automatically over self-reported ones.",
      icon: "list-ordered",
      content: [
        "verifyFileOrder() / verifyFileDetachedOrder() accept an expectedOrder array — MajikKey instances and/or ExpectedSigner objects, mixed freely and normalized internally — where array position is the expected chronological position.",
        "Each expected signer is verified against the public keys you supply, not the keys self-asserted inside their own envelope entry — tying identity to a key you actually trust rather than whatever the signature claims to be signed with.",
        "Order comparisons only run between signers who both signed and produced a valid signature — an invalid signature's timestamp isn't trustworthy, so it's excluded from ordering but still reported.",
        "resolveEffectiveTimestamp() prefers tsa.payload.timestamp (server-attested) when present, falling back to the self-reported timestamp field. Every result flags usesUnattestedTimestamp when any comparison relied on a self-reported clock.",
        "Identical timestamps between two signers produce a soft warning (softTieWarnings) rather than a hard failure — order between them is genuinely indistinguishable, not violated.",
        "An optional strict mode fails the result if any signer present in the envelope is absent from expectedOrder — useful when the complete signer set matters, not just the relative order of the signers you named.",
        "The result reports four independent, co-occurring signals: pending signers, invalid/tampered signatures, order violations, and — when everything else passes — a soft note about tied timestamps.",
      ],
      codeExample: {
        title: "Verify Bob signed before Dave",
        language: "typescript",
        code: "const result = await MajikSignature.verifyFileOrder(signedBlob, [bobKey, daveKey]);\n\nif (!result.allExpectedSigned) {\n  console.log('Still pending:', result.pendingSigners);\n} else if (!result.allValid) {\n  console.log('Invalid/tampered:', result.invalidSigners);\n} else if (!result.orderRespected) {\n  console.log('Wrong order:', result.violations);\n} else {\n  console.log('Signed in order, all valid.');\n}\n\n// Strict mode — fail if anyone outside expectedOrder signed at all\nconst strict = await MajikSignature.verifyFileOrder(signedBlob, [bobKey, daveKey], {\n  strict: true,\n});",
      },
      benefits: [
        "Enforces approval sequences (requester before approver, legal before finance) as a first-class, independently verifiable check",
        "Distinguishes cryptographically strong (TSA-attested) order proof from a self-reported claim",
        "Composable with, not a replacement for, standard signature verification — it reuses the same crypto check internally",
      ],
    },
    {
      id: "trusted-timestamps",
      title: "Trusted Timestamps (TSA)",
      description:
        "A MajikSignature can carry an optional MajikTimestamp — a signature from a timestamp authority binding a content digest, a server-generated nonce, and a server-authoritative time. The TSA's own signature is a full hybrid Majik Signature.",
      icon: "clock",
      content: [
        'The TSA payload is domain-separated with "majikah-tsa-v-1:" and binds { digest, nonce, timestamp, tsa: { id, signerFingerprint } }.',
        "The client builds a MajikTSARequest from an existing signature via buildTSARequestPayload() and sends it to a TSA server.",
        "The TSA server signs the payload with its own MajikKey via signTSA(), returning a complete MajikTimestamp (including the server-generated nonce and timestamp).",
        "The client attaches it via signature.addTSA(timestamp) — this validates the digest match and the TSA's own signature before accepting it. A TSA, once attached, cannot be replaced.",
        "signature.verifyTSA() re-verifies the attached TSA's signature independently, useful after deserializing a signature from storage.",
        "A TSA timestamp can also be attached during detached signing via signFileDetached()'s tsa option, and is what signing-order verification prefers when computing chronological sequence.",
      ],
      codeExample: {
        title: "Request and attach a trusted timestamp",
        language: "typescript",
        code: "const signature = await MajikSignature.sign(document, key);\nconst tsaRequest = signature.buildTSARequestPayload();\n\n// Server side, using a TSA-controlled MajikKey\nconst timestamp = await MajikSignature.signTSA(tsaRequest, tsaKey, {\n  id: 'tsa.majikah.solutions',\n  signerFingerprint: tsaKey.fingerprint,\n});\n\n// Client attaches and validates\nsignature.addTSA(timestamp);\nconsole.log('Has TSA:', signature.hasTSA);\nconsole.log('TSA still valid:', signature.verifyTSA().valid);",
      },
    },
    {
      id: "content-agnostic-hashing",
      title: "Content-Agnostic Hashing",
      description:
        "Content is never embedded in the signature envelope — only its SHA-256 hash is signed. This means any content size or format is supported identically, and a 500 MB video signs at the same speed as a 10-byte string.",
      icon: "hash",
      content: [
        "The SHA-256 hash is computed over the raw bytes of the content — UTF-8 strings are encoded to Uint8Array before hashing.",
        "The hash is included in the canonical payload and is therefore bound by both signatures.",
        "Any byte change to the content — even a single bit flip — produces a different hash and invalidates both signatures.",
        "This approach is content-agnostic: plaintext, JSON, binary, images, audio, video, documents — all are hashed and signed the same way.",
        "The signature envelope size is fixed (~10 KB JSON per signer) regardless of content size.",
      ],
      benefits: [
        "Constant-time signing regardless of content size",
        "No content size limits — sign gigabyte files as easily as kilobyte strings",
        "Format-independent — the same API works for any content type",
      ],
    },
    {
      id: "file-embedding",
      title: "Native File Embedding",
      description:
        "Majik Signature can embed itself directly into a file's native container. No sidecar files needed — the signature lives inside the file, and the file remains valid in its original format.",
      icon: "file",
      content: [
        "Tier 1 formats (PDF, PNG, WAV, MP3, MP4/MOV, DOCX/XLSX/PPTX/ODF, MKV/WebM, and text-based formats) use each format's native metadata or container mechanism.",
        "Tier 2 (any other format) uses a universal, self-describing binary trailer that can be cleanly detected and stripped regardless of file type.",
        "Embedding is always idempotent — calling signFile() on an already-signed file strips the old envelope and embeds a fresh one.",
        "The signature always covers the original file bytes before embedding. Verification strips the envelope before re-hashing, so the round-trip is always: sign(originalBytes) → embed → extract → strip → verify(originalBytes).",
        "File format is auto-detected via magic bytes and MIME type sniffing — no manual hints required in most cases.",
        "Office/ODF formats (ZIP-based) are always fully re-canonicalized through the same deterministic zipping pipeline at both sign- and verify-time — strip() always rebuilds the ZIP via a full unzip/rezip pass, even when no signature entry exists yet — so identical content always strips to identical bytes regardless of when it was last re-zipped.",
        "The MP4/MOV handler additionally preserves box order and byte-for-byte trailing/leftover data at every nesting level (top-level, moov children, udta children) rather than discarding anything it doesn't recognize.",
      ],
      table: {
        headers: ["Format", "Embedding Mechanism"],
        rows: [
          {
            cells: [
              "PDF",
              "Binary trailer appended after the last %%EOF marker (PDF 1.7 §7.5.6 compliant)",
            ],
          },
          {
            cells: ["PNG", "iTXt metadata chunk"],
          },
          {
            cells: ["WAV", "RIFF LIST INFO chunk, custom ISIG sub-chunk"],
          },
          {
            cells: ["MP3", "ID3v2 TXXX frame"],
          },
          {
            cells: ["MP4/MOV/M4A/M4V", "moov → udta → majk box"],
          },
          {
            cells: [
              "DOCX/XLSX/PPTX/ODT/ODS/ODP",
              "Dedicated majik-signature.json entry inside the ZIP container",
            ],
          },
          {
            cells: ["MKV/WebM", "Custom Matroska metadata tag"],
          },
          {
            cells: ["JPEG, FLAC", "Native format metadata"],
          },
          {
            cells: [
              "HTML, Markdown, JSON, plain text, source code",
              "Appended, format-appropriate metadata block",
            ],
          },
          {
            cells: [
              "Any other format",
              "Universal binary trailer: [original][signature JSON][8-byte length][8-byte magic 'MAJIKSIG']",
            ],
          },
        ],
      },
    },
    {
      id: "detached-signing-envelope",
      title: "Detached Signing & MajikSignatureEnvelope",
      description:
        "Sign a file and receive the resulting envelope separately from the file bytes — for verification pipelines where the payload and its signatures travel or are stored independently.",
      icon: "layers",
      content: [
        "signFileDetached() returns the clean, stripped file bytes and the envelope as two separate values — nothing is embedded.",
        "verifyFileDetached() strips the target file first (in case it also happens to carry an embedded envelope), then verifies against the separately-supplied envelope, so the two never interfere.",
        "A detached envelope in progress can be continued across multiple signFileDetached() calls via the existingEnvelope option, letting several parties sign before anything is ever embedded.",
        "MajikSignatureEnvelope is the immutable, behavior-rich in-memory class backing every detached and embedded envelope — assertCanSign(), withSignature(), withAllowlist(), withSeal(), withChainAnchor(), getSignatories(), resolveIssuer(), verifySeal(), and more all live here.",
        "MajikSignatureEnvelope.from(input) is a universal entry point accepting an instance, its plain JSON shape, raw .mjksig bytes, or a .mjksig Blob — every detached-envelope-accepting method normalizes through it internally.",
        "Detached envelopes serialize to plain JSON, compact base64, or the dedicated self-describing .mjksig binary container (magic bytes + version + length-prefixed payload) intended for on-disk storage or out-of-band transport.",
      ],
      codeExample: {
        title: "Sign detached, continue with a second signer",
        language: "typescript",
        code: "const { blob, envelope } = await MajikSignature.signFileDetached(file, aliceKey);\n\n// Store however you like\nconst mjksigBlob = envelope.toMJKSIG();\n\n// Bob continues signing the same detached envelope\nconst { envelope: envelope2 } = await MajikSignature.signFileDetached(blob, bobKey, {\n  existingEnvelope: envelope,\n});\n\nconst results = await MajikSignature.verifyFileDetached(blob, envelope2, aliceKey);",
      },
    },
    {
      id: "batch-signing-mjksmap",
      title: "Batch Signing & MajikSignatureMap",
      description:
        "Sign every file in a folder, project export, or zip's contents against a single manifest instead of tracking one signature file per asset — resilient to files being renamed or relocated after signing.",
      icon: "folder",
      content: [
        "signBatchDetached() signs a list of { path, blob } entries, reusing signFileDetached() per file internally — no duplicated crypto path.",
        "Default mode 'map' packages the whole batch as one MajikSignatureMap, ready to store as a .mjksmap manifest. Mode 'separate' instead produces one .mjksig Blob per file.",
        "continueOnError (default false) controls whether a single unreadable/unsignable file aborts the entire batch or is collected in a failures array while the rest proceed.",
        "MajikSignatureMap is keyed by path, not content hash alone — duplicate-content files across a batch are legitimate and must not silently collide.",
        "resolveEntry(path, file) tries the exact path first, then falls back to a content-hash search — a file renamed or moved after signing is still found and reported as 'relocated' rather than missing.",
        "verifyFilesFromMjksMap() never throws per-file by default: every outcome ('verified' / 'invalid' / 'tampered' / 'not_in_map') is reported so a full status table can be rendered in one pass. summarizeBatchVerification() gives a one-glance pass/fail count.",
        "Like MJKSIG, .mjksmap is a self-describing binary container — magic bytes, version, length-prefixed JSON payload — validated before any parsing is attempted.",
      ],
      codeExample: {
        title: "Sign and verify a batch",
        language: "typescript",
        code: "const result = await MajikSignature.signBatchDetached(\n  [\n    { path: 'docs/report.pdf', blob: reportBlob },\n    { path: 'docs/appendix.pdf', blob: appendixBlob },\n  ],\n  aliceKey,\n);\n\nif (result.mode === 'map') {\n  zip.file('signatures.mjksmap', await result.mapBlob.arrayBuffer());\n}\n\n// Later, verify an extracted batch — tolerant of renamed/relocated files\nconst results = await MajikSignature.verifyFilesFromMjksMap(map, extractedFiles, publicKeys);\nconst summary = MajikSignature.summarizeBatchVerification(results);\nconsole.log(`${summary.verified}/${summary.total} verified`);",
      },
      benefits: [
        "One manifest instead of N loose signature files for an entire archive or export",
        "Verification survives files being reorganized after signing, without treating that as tampering",
        "Per-file reporting suitable for rendering a full batch status table in a UI",
      ],
    },
    {
      id: "chain-anchoring",
      title: "Chain Anchoring",
      description:
        "A sealed envelope's seal hash can be committed to an external blockchain and the resulting confirmed anchor registered back into the envelope, for independent, external notarization of a finalized document.",
      icon: "link",
      content: [
        "canAnchor() checks eligibility — anchoring requires the envelope to already be sealed.",
        "buildChainAnchorMemo(sealHash) builds the canonical, domain-separated memo string ('majik-notary-v-1:' + sealHash) intended for submission on-chain by an external integration layer. The SDK never talks to any chain itself.",
        "registerChainAnchor(file, anchor) embeds an already-confirmed MajikChainAnchor into the envelope — the caller is responsible for submitting and confirming the transaction beforehand. Upserts by anchor.id, so retried registration doesn't produce duplicates.",
        "getChainAnchors() reads back all anchors currently registered on a file's envelope.",
        "MajikSignatureEnvelope.withChainAnchor() enforces at the structural level that the envelope must already be sealed, and that the anchor's digest matches the current seal hash — guarding against embedding an anchor computed against a stale seal.",
      ],
      codeExample: {
        title: "Anchor a sealed envelope",
        language: "typescript",
        code: "const { permitted } = await MajikSignature.canAnchor(sealedBlob);\n\n// Build the memo to submit on-chain elsewhere (e.g. via a chain-integration layer)\nconst memo = MajikSignature.buildChainAnchorMemo(sealInfo.sealHash);\n\n// Once the transaction is externally confirmed, register the anchor\nconst blob = await MajikSignature.registerChainAnchor(sealedBlob, confirmedAnchor);\n\nconst anchors = await MajikSignature.getChainAnchors(blob);",
      },
    },
    {
      id: "public-key-verification",
      title: "Public-Key Verification",
      description:
        "Verification requires only the signer's public keys — no private key is ever needed. This makes verification safe in any context, including untrusted environments, public APIs, and client-side applications.",
      icon: "key",
      content: [
        "Every per-signer envelope is self-contained: it includes the signer's Ed25519 and ML-DSA-87 public keys alongside the signatures.",
        "Anyone with the signature envelope and the original content can verify without contacting the signer or requiring any secrets.",
        "Public keys can be extracted from a MajikKey instance (locked or unlocked) via publicKeysFromMajikKey(), or from an existing signature envelope itself via signature.extractPublicKeys() — useful when only a serialized signature is available (e.g. to independently verify a TSA token).",
        "Verification is a pure function — no state, no side effects, no network calls.",
        "The signer's private keys never leave the signer's device and are never needed for verification.",
        "signature.isValid() / envelope.isValid() provide a cheap, non-throwing structural sanity check after deserializing from storage, ahead of full cryptographic verification.",
      ],
      codeExample: {
        title: "Verify with public keys only",
        language: "typescript",
        code: "// Extract public keys from a MajikKey (works on locked keys)\nconst publicKeys = MajikSignature.publicKeysFromMajikKey(key);\n\n// Verify using only public keys — no private key needed\nconst result = MajikSignature.verify(content, signature, publicKeys);\nconsole.log('Valid:', result.valid);\n\n// Or verify directly against a MajikKey instance (convenience)\nconst result2 = MajikSignature.verifyWithKey(content, signature, key);\n\n// Or extract public keys from the signature envelope itself\nconst embeddedKeys = signature.extractPublicKeys();",
      },
    },
    {
      id: "serialization",
      title: "JSON & Base64 Serialization",
      description:
        "MajikSignature and MajikSignatureEnvelope instances serialize to a well-defined JSON shape, a compact base64 string, or a dedicated binary container. The serialized form is safe to store in databases, transmit over HTTP, or embed in file metadata.",
      icon: "file",
      content: [
        "toJSON() exports the full signature envelope as a plain JavaScript object: version, signerId, public keys, content hash, timestamp, content type, both signatures, and — when applicable — allowlistHash and tsa (all base64-encoded, except tsa's internal digest/nonce fields).",
        "serialize() encodes the JSON as a compact base64 string suitable for HTTP headers, database fields, or sidecar files.",
        "fromJSON(json) and deserialize(base64) reconstruct a fully typed MajikSignature instance from stored data.",
        "At the multi-signer level, MajikSignatureEnvelope carries the same toJSON()/serialize()/deserialize() round-trip, plus toMJKSIG()/fromMJKSIG() for the versioned binary container format, intended for on-disk storage or out-of-band transport.",
        "The JSON envelope is approximately 10 KB (minified) per signer, dominated by the ML-DSA-87 signature (~6 KB base64) and public key (~3.5 KB base64).",
        "No plaintext secrets are ever included in the serialized form — only public keys and signatures.",
      ],
      codeExample: {
        title: "Serialize and reconstruct",
        language: "typescript",
        code: "const signature = await MajikSignature.sign(content, key);\n\n// Serialize to JSON\nconst json = signature.toJSON();\nawait db.signatures.insert({ docId, sig: json });\n\n// Serialize to base64 (compact)\nconst base64 = signature.serialize();\nres.setHeader('X-Majik-Signature', base64);\n\n// Reconstruct later\nconst sig1 = MajikSignature.fromJSON(json);\nconst sig2 = MajikSignature.deserialize(base64);",
      },
    },
    {
      id: "tamper-detection",
      title: "Tamper Detection",
      description:
        "Any modification to the signed content — even a single bit flip — invalidates both signatures. This provides strong integrity guarantees and makes tampering immediately detectable.",
      icon: "alert",
      content: [
        "The SHA-256 content hash is part of the canonical payload signed by both algorithms.",
        "On verification, the content is re-hashed and compared to the hash in the envelope. If they don't match, the signature is rejected before even attempting cryptographic verification.",
        "Because the hash is signed, an attacker cannot replace the content and update the hash without re-signing with the private keys.",
        "This protects against accidental corruption (file transfer errors, disk corruption) and intentional tampering.",
        "For embedded or detached signatures, the signature always covers the original file bytes before embedding, so the embedding (or lack thereof) itself cannot be tampered with.",
        "The same integrity model extends to allowlists (verifyAllowlistIntegrity()), seals (verifySeal()), and batch manifest entries (resolveEntry() detecting 'path_tampered').",
      ],
      codeExample: {
        title: "Tamper detection example",
        language: "typescript",
        code: "const signature = await MajikSignature.sign('Original text', key);\n\n// Verify original content\nconst result1 = MajikSignature.verifyWithKey('Original text', signature, key);\nconsole.log('Original valid:', result1.valid); // true\n\n// Attempt to verify tampered content\nconst result2 = MajikSignature.verifyWithKey('Modified text', signature, key);\nconsole.log('Tampered valid:', result2.valid); // false",
      },
    },
    {
      id: "idempotent-re-signing",
      title: "Idempotent Re-Signing",
      description:
        "Files can be safely re-signed at any time. Existing envelopes are automatically stripped before signing, preventing signature accumulation and ensuring the new signature always covers the original content.",
      icon: "refresh",
      content: [
        "signFile() always strips any existing envelope before computing the new signature.",
        "This means re-signing is idempotent: signing an already-signed file replaces the prior envelope rather than stacking on top of it.",
        "The new signature always covers the original file bytes, not the previously signed file.",
        "This is safe for workflows where files are re-signed by different parties or at different times (e.g., counter-signing, version updates) — subject to allowlist/seal restrictions if configured.",
        "The stripping process is format-aware — it uses the same handlers as extraction and embedding.",
      ],
    },
    {
      id: "image-stamping-experimental",
      title: "Image Stamping (Experimental)",
      description:
        "Compression-resistant signature embedding directly into image pixel/frequency data, for cases where standard metadata may be stripped by re-compression.",
      icon: "image",
      content: [
        "stampImage(), verifyStamp(), inspectStamp(), and isStamped() are explicitly marked @experimental in source.",
        "This is not the same mechanism as the PNG iTXt metadata handler — it targets durability against lossy re-compression rather than metadata-preserving tools.",
        "API shape is not yet considered stable and may change across minor versions ahead of 1.0.",
      ],
      benefits: [
        "Aims to survive re-compression that would strip standard metadata-based embedding",
      ],
    },
  ],
  useCases: [
    {
      title: "Content Provenance for Creators",
      description:
        "Musicians, artists, photographers, and writers can sign their work to prove authorship. The signature embeds directly in the file — MP3s, PNGs, PDFs — and travels with it through distribution.",
      scenario:
        "A photographer signs their portfolio images with their MajikKey. Each PNG carries an embedded signature in its iTXt chunk. Buyers or galleries can verify the photo came from the photographer's identity without contacting them.",
      benefits: [
        "Proof of authorship that travels with the file",
        "No reliance on central registries or blockchain",
        "Signature detectable with standard verification tooling",
      ],
      icon: "image",
    },
    {
      title: "Multi-Party Contract Signing",
      description:
        "Sign legal documents, contracts, or agreements that require independent sign-off from more than one party, with a final seal once every required signature is in place.",
      scenario:
        "Two parties independently sign a PDF contract with their own MajikKeys. Once both have signed, the issuing party seals the envelope, permanently locking out further signatures. Either party — or a third-party auditor — can verify who signed, when, and that the seal itself hasn't been tampered with.",
      benefits: [
        "Real independent signatures per party, not one signature 'on behalf of' several people",
        "Non-repudiation — no signer can deny signing",
        "Sealing prevents late, unauthorized additions to the signer list",
      ],
      codeExample: {
        title: "Two-party contract with a seal",
        language: "typescript",
        code: "const { blob: step1 } = await MajikSignature.signFile(contractPdf, partyAKey, {\n  expectedSigners: [\n    MajikSignature.expectedSignerFromKey(partyAKey),\n    MajikSignature.expectedSignerFromKey(partyBKey),\n  ],\n});\n\nconst { blob: step2 } = await MajikSignature.signFile(step1, partyBKey);\nconst { blob: sealed } = await MajikSignature.seal(step2, partyAKey);\n\n// Auditor verification\nconst sigResults = await MajikSignature.verifyFile(sealed, partyAKey);\nconst sealResult = await MajikSignature.verifySeal(sealed);\nconsole.log('All signatures valid:', sigResults.every((r) => r.valid));\nconsole.log('Seal valid:', sealResult.valid);",
      },
      icon: "file",
    },
    {
      title: "Approval Order Enforcement",
      description:
        "Some workflows require not just multiple signatures, but a specific sequence — a request must be approved by a manager before finance signs off, or an author's draft must be countersigned by legal only after their own signature exists.",
      scenario:
        "An internal expense-approval PDF is signed by the requester, then routed for manager approval. An automated compliance check verifies both that the manager is on the allowlist and that the manager's signature timestamp — ideally TSA-attested — comes after the requester's, rejecting any record where the sequence was violated even if both signatures are individually valid.",
      benefits: [
        "Catches a valid-but-out-of-sequence signature that simple presence-based verification would miss",
        "Distinguishes strongly-attested (TSA) order proof from a self-reported timestamp claim",
        "Composable with allowlists and sealing for a full approval-workflow guarantee",
      ],
      codeExample: {
        title: "Enforce requester-then-approver order",
        language: "typescript",
        code: "const orderResult = await MajikSignature.verifyFileOrder(expensePdf, [\n  requesterKey,\n  managerKey,\n]);\n\nif (!orderResult.valid) {\n  throw new Error(orderResult.reason ?? 'Approval sequence not satisfied');\n}",
      },
      icon: "check-circle",
    },
    {
      title: "Batch Asset Signing for Digital Archives",
      description:
        "Sign every file in a project export, digital archive, or zip's contents against a single manifest, resilient to files being reorganized, renamed, or relocated after signing.",
      scenario:
        "A design studio exports a completed project as a folder of assets (source files, renders, deliverables) and signs the entire batch in one call, embedding a signatures.mjksmap manifest at the root of the archive. Years later, when the archive has been reorganized into new subfolders, verification still finds and validates every file by content hash.",
      benefits: [
        "One manifest instead of tracking N loose signature files",
        "Verification survives reasonable archive reorganization without false 'tampered' results",
        "Per-file status reporting suitable for a full archive-integrity dashboard",
      ],
      icon: "folder",
    },
    {
      title: "On-Chain Notarization of Sealed Records",
      description:
        "Anchor a sealed, multi-party envelope's hash commitment to an external blockchain for independent, tamper-evident notarization beyond what the library's own cryptography provides.",
      scenario:
        "A finalized, sealed contract's seal hash is submitted as a memo to a public blockchain by a companion notarization service. Once the transaction confirms, the resulting anchor is registered back into the file's envelope, giving any future verifier both the cryptographic seal proof and an independently checkable, timestamped on-chain record.",
      benefits: [
        "Independent, third-party-verifiable timestamp beyond what any single organization asserts",
        "The SDK's role stays narrow and auditable — it only builds the memo and registers a confirmed result, it never custodies chain credentials",
        "Anchors are digest-bound to a specific seal hash, so a stale or mismatched anchor is rejected structurally",
      ],
      icon: "link",
    },
    {
      title: "API Payload Signing",
      description:
        "Sign JSON API responses or requests for non-repudiation. The signature proves the payload originated from a specific server or client and has not been altered in transit.",
      scenario:
        "A financial API signs transaction responses with a server-side MajikKey. Clients verify the signature to ensure the transaction record is authentic and has not been tampered with by a MITM.",
      benefits: [
        "End-to-end integrity — detect tampering between server and client",
        "Non-repudiation for audit trails",
        "Works alongside TLS — defense-in-depth",
      ],
      codeExample: {
        title: "Sign JSON API response",
        language: "typescript",
        code: "// Server: sign response payload\nconst payload = { userId: 'usr_123', balance: 1000, currency: 'USD' };\nconst signature = await MajikSignature.sign(JSON.stringify(payload), serverKey, {\n  contentType: CONTENT_TYPES.JSON,\n});\n\nres.json({ data: payload, signature: signature.toJSON() });\n\n// Client: verify\nconst result = MajikSignature.verify(\n  JSON.stringify(response.data),\n  response.signature,\n  serverPublicKeys\n);\nif (!result.valid) throw new Error('Tampered response');",
      },
      icon: "code",
    },
    {
      title: "Compliance Records with Trusted Timestamps",
      description:
        "Bind a document's signing time to a server-authoritative, independently signed timestamp — strengthening proof of when signing occurred beyond a self-reported client timestamp.",
      scenario:
        "A regulated business signs an invoice locally, then requests a Trusted Timestamp from a TSA service to attach to the signature before archiving the record for compliance purposes.",
      benefits: [
        "Server-authoritative timestamp, not self-reported by the signer alone",
        "TSA signature itself carries the same hybrid Ed25519 + ML-DSA-87 guarantees",
        "Digest-bound — the TSA attests to a specific content hash, not just 'a document'",
      ],
      icon: "clock",
    },
    {
      title: "Software Distribution",
      description:
        "Sign release artifacts (binaries, installers, packages) to prove they originated from the official developer. Users verify before installation to detect supply-chain attacks or malware injection.",
      scenario:
        "A developer signs their app's installer binary with their MajikKey. The signature is embedded via the universal trailer. Users run a verification tool before installing to confirm authenticity.",
      benefits: [
        "Protect against supply-chain attacks",
        "Users can verify without trusting third-party certificate authorities",
        "Post-quantum secure signatures resist future quantum threats to code signing",
      ],
      icon: "package",
    },
    {
      title: "Media Certification",
      description:
        "Audio and video files can be signed to certify they are authentic originals and have not been deepfaked, edited, or tampered with.",
      scenario:
        "A journalist signs raw video footage at the scene with their device's MajikKey. News organizations verify the signature embedded in the MP4 moov/udta box to confirm the footage is an unaltered original.",
      benefits: [
        "Combats deepfakes and synthetic media",
        "Chain-of-custody for investigative journalism",
        "Signature survives standard video player round-trips (MP4 metadata)",
      ],
      codeExample: {
        title: "Sign video footage",
        language: "typescript",
        code: "const { blob: signedVideo } = await MajikSignature.signFile(rawFootage, journalistKey, {\n  contentType: 'video/mp4',\n});\n\n// Upload to newsroom server\nawait uploadToNewsroom(signedVideo);\n\n// Newsroom verifies\nconst results = await MajikSignature.verifyFile(signedVideo, journalistPublicKeys);\nif (results.every((r) => r.valid)) {\n  console.log('Authentic footage from:', results[0].signerId);\n  console.log('Captured at:', results[0].timestamp);\n}",
      },
      icon: "video",
    },
  ],
  examples: [
    {
      title: "Basic Content Signing",
      description: "Sign and verify plaintext, JSON, or binary content.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Sign and verify a string",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikSignature, CONTENT_TYPES } from '@majikah/majik-signature';\n\nconst mnemonic = MajikKey.generateMnemonic();\nconst key = await MajikKey.create(mnemonic, 'passphrase', 'My Key');\n\nconst message = 'This is a signed message.';\nconst signature = await MajikSignature.sign(message, key, {\n  contentType: CONTENT_TYPES.TEXT,\n});\n\nconst result = MajikSignature.verifyWithKey(message, signature, key);\nconsole.log('Valid:', result.valid);      // true\nconsole.log('Signed by:', result.signerId);",
        },
        {
          title: "Sign JSON payload",
          language: "typescript",
          code: "const payload = { userId: 'usr_123', action: 'transfer', amount: 1000 };\nconst content = JSON.stringify(payload);\n\nconst signature = await MajikSignature.sign(content, key, {\n  contentType: CONTENT_TYPES.JSON,\n});\n\nconst result = MajikSignature.verifyWithKey(content, signature, key);\nconsole.log('Valid:', result.valid); // true",
        },
        {
          title: "Sign binary data",
          language: "typescript",
          code: "const binaryData = new Uint8Array([0x48, 0x65, 0x6c, 0x6c, 0x6f]);\n\nconst signature = await MajikSignature.sign(binaryData, key);\nconst result = MajikSignature.verifyWithKey(binaryData, signature, key);\nconsole.log('Valid:', result.valid); // true",
        },
      ],
    },
    {
      title: "File Signing & Embedding",
      description: "Sign files and embed signatures into their containers.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Sign and embed into a PDF",
          language: "typescript",
          code: "const { blob: signedPdf } = await MajikSignature.signFile(pdfBlob, key);\n\n// signedPdf is a valid PDF — the signature is appended as a binary trailer\n// after the file's %%EOF marker (PDF 1.7 §7.5.6 compliant)\n\n// Save or upload\nawait saveFile('signed-document.pdf', signedPdf);",
        },
        {
          title: "Sign audio file (WAV)",
          language: "typescript",
          code: "const { blob: signedWav, handler } = await MajikSignature.signFile(wavBlob, key);\n\nconsole.log('Signed using handler:', handler); // \"WAV\"\n\n// The signature is embedded in a RIFF LIST INFO / ISIG sub-chunk\n// The WAV remains playable in all standard audio players",
        },
        {
          title: "Sign video file (MP4)",
          language: "typescript",
          code: "const { blob: signedMp4 } = await MajikSignature.signFile(mp4Blob, key, {\n  contentType: 'video/mp4',\n});\n\n// Signature is embedded in moov → udta → majk box\n// Video remains playable in VLC, QuickTime, etc.",
        },
      ],
    },
    {
      title: "Verification Workflows",
      description: "Verify signatures against content or extract from files.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Verify all embedded signatures in a file",
          language: "typescript",
          code: "const results = await MajikSignature.verifyFile(signedFile, key);\n\nif (results.every((r) => r.valid)) {\n  console.log('All signatures verified');\n  for (const r of results) {\n    console.log('Signed by:', r.signerId, 'at', r.timestamp);\n  }\n} else {\n  console.log('One or more signatures invalid or tampered');\n}",
        },
        {
          title: "Extract signatures without verifying",
          language: "typescript",
          code: "const signatures = await MajikSignature.extractFrom(signedFile);\n\nfor (const sig of signatures) {\n  console.log('Signer:', sig.signerId);\n  console.log('Timestamp:', sig.timestamp);\n  console.log('Content type:', sig.contentType);\n}\nif (signatures.length === 0) {\n  console.log('No signature found');\n}",
        },
        {
          title: "Check if file is signed (fast guard)",
          language: "typescript",
          code: "if (await MajikSignature.isSigned(file)) {\n  console.log('File contains a signature envelope');\n  const results = await MajikSignature.verifyFile(file, key);\n} else {\n  console.log('No signature present');\n}",
        },
      ],
    },
    {
      title: "Multi-Signature, Allowlists & Sealing",
      description:
        "Restrict who can sign, track signing status, and finalize a document.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Establish an allowlist and track status",
          language: "typescript",
          code: "const { blob: step1 } = await MajikSignature.signFile(file, aliceKey, {\n  expectedSigners: [\n    MajikSignature.expectedSignerFromKey(aliceKey),\n    MajikSignature.expectedSignerFromKey(bobKey),\n  ],\n});\n\nconst { signed, pending } = await MajikSignature.getSignatories(step1);\nconsole.log('Pending signers:', pending.map((s) => s.signerId));\n\n// Check permission before attempting to sign\nconst { permitted, reason } = await MajikSignature.canSign(step1, eveKey);\nconsole.log('Eve permitted:', permitted, reason);",
        },
        {
          title: "Full envelope introspection",
          language: "typescript",
          code: "const info = await MajikSignature.getEnvelopeInfo(file);\nif (info) {\n  console.log('Is multi-sig:', info.isMultiSig);\n  console.log('Is sealed:', info.isSealed);\n  console.log('Issuer:', info.issuer?.signerId);\n  console.log('Signature count:', info.signatureCount);\n}",
        },
        {
          title: "Seal an envelope and verify the seal",
          language: "typescript",
          code: "const { blob: sealed, sealInfo } = await MajikSignature.seal(file, issuerKey);\n\nconst sealResult = await MajikSignature.verifySeal(sealed);\nconsole.log('Seal valid:', sealResult.valid);\nconsole.log('Sealed by:', sealResult.sealedBy, 'at', sealResult.sealTimestamp);",
        },
      ],
    },
    {
      title: "Detached Signing & Envelopes",
      description:
        "Produce and verify signature envelopes that travel independently of the file.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Sign detached and store the envelope separately",
          language: "typescript",
          code: "const { blob, envelope, signature } = await MajikSignature.signFileDetached(file, aliceKey);\n\n// blob is the clean file — envelope travels separately\nawait db.envelopes.insert({ fileId, envelope: envelope.toJSON() });\nawait storage.upload(fileId, blob);",
        },
        {
          title: "Continue a detached envelope with a second signer",
          language: "typescript",
          code: "const stored = await db.envelopes.findOne({ fileId });\nconst existingEnvelope = await MajikSignatureEnvelope.from(stored.envelope);\n\nconst { envelope } = await MajikSignature.signFileDetached(fileBlob, bobKey, {\n  existingEnvelope,\n});\n\nawait db.envelopes.update({ fileId }, { envelope: envelope.toJSON() });",
        },
        {
          title: "Verify against a detached .mjksig envelope",
          language: "typescript",
          code: "const results = await MajikSignature.verifyFileDetached(\n  fileBlob,\n  mjksigBlob, // accepts instance, JSON, bytes, or Blob\n  publicKeys,\n);\nconsole.log('All valid:', results.every((r) => r.valid));",
        },
      ],
    },
    {
      title: "Batch Signing & Verification",
      description: "Sign and verify an entire folder or archive in one call.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Sign a batch as one manifest",
          language: "typescript",
          code: "const result = await MajikSignature.signBatchDetached(\n  [\n    { path: 'assets/logo.png', blob: logoBlob },\n    { path: 'assets/banner.png', blob: bannerBlob },\n  ],\n  aliceKey,\n  { continueOnError: true },\n);\n\nconsole.log('Failures:', result.mode === 'map' ? result.failures : []);",
        },
        {
          title: "Verify a batch, tolerating relocated files",
          language: "typescript",
          code: "const map = await MajikSignatureMap.fromMJKSMAP(mapBlob);\nconst results = await MajikSignature.verifyFilesFromMjksMap(map, extractedFiles, publicKeys);\n\nfor (const r of results) {\n  if (r.status === 'not_in_map') console.log('Missing:', r.path);\n  if (r.relocatedFrom) console.log(`${r.path} was originally signed as ${r.relocatedFrom}`);\n}\n\nconst summary = MajikSignature.summarizeBatchVerification(results);\nconsole.log('All valid:', summary.allValid);",
        },
      ],
    },
    {
      title: "Signature Order Verification",
      description:
        "Verify signers signed in a required chronological sequence.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Verify a two-party expected order",
          language: "typescript",
          code: "const result = await MajikSignature.verifyFileOrder(signedBlob, [requesterKey, approverKey]);\n\nif (result.softTieWarnings.length > 0) {\n  console.log('Note:', result.reason); // valid, but timestamps tied\n}\nif (result.usesUnattestedTimestamp) {\n  console.log('Order relied on a self-reported (non-TSA) timestamp.');\n}",
        },
        {
          title: "Strict mode — reject unexpected extra signers",
          language: "typescript",
          code: "const strictResult = await MajikSignature.verifyFileOrder(\n  signedBlob,\n  [requesterKey, approverKey],\n  { strict: true },\n);\n\nif (!strictResult.valid && strictResult.unexpectedSigners.length > 0) {\n  console.log('Unexpected signer(s):', strictResult.unexpectedSigners);\n}",
        },
      ],
    },
    {
      title: "Chain Anchoring",
      description:
        "Register a confirmed blockchain anchor on a sealed envelope.",
      category: "Advanced",
      difficulty: "advanced",
      examples: [
        {
          title: "Build a memo and register a confirmed anchor",
          language: "typescript",
          code: "const { permitted, reason } = await MajikSignature.canAnchor(sealedBlob);\nif (!permitted) throw new Error(reason);\n\nconst memo = MajikSignature.buildChainAnchorMemo(sealInfo.sealHash);\n// ...submit memo on-chain via an external integration, await confirmation...\n\nconst anchoredBlob = await MajikSignature.registerChainAnchor(sealedBlob, confirmedAnchor);\nconst anchors = await MajikSignature.getChainAnchors(anchoredBlob);",
        },
      ],
    },
    {
      title: "Trusted Timestamps",
      description: "Request and attach a TSA-signed timestamp.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Client request + server issuance",
          language: "typescript",
          code: "const signature = await MajikSignature.sign(document, key);\nconst request = signature.buildTSARequestPayload();\n\n// TSA server\nconst timestamp = await MajikSignature.signTSA(request, tsaKey, {\n  id: 'tsa.majikah.solutions',\n  signerFingerprint: tsaKey.fingerprint,\n});\n\n// Client\nsignature.addTSA(timestamp);\nconsole.log('TSA attached:', signature.hasTSA);",
        },
      ],
    },
    {
      title: "Advanced: Serialization & Storage",
      description: "Store signatures in databases or transmit over HTTP.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Store signature in database",
          language: "typescript",
          code: "const signature = await MajikSignature.sign(content, key);\n\n// Store as JSON\nconst json = signature.toJSON();\nawait db.signatures.insert({ docId: 'doc_123', sig: json });\n\n// Load and verify later\nconst stored = await db.signatures.findOne({ docId: 'doc_123' });\nconst sig = MajikSignature.fromJSON(stored.sig);\nconst result = MajikSignature.verifyWithKey(content, sig, key);",
        },
        {
          title: "Transmit signature in HTTP header",
          language: "typescript",
          code: "// Server: attach signature to response\nconst signature = await MajikSignature.sign(responseBody, serverKey);\nres.setHeader('X-Majik-Signature', signature.serialize());\nres.send(responseBody);\n\n// Client: verify\nconst sigBase64 = res.headers['x-majik-signature'];\nconst signature = MajikSignature.deserialize(sigBase64);\nconst result = MajikSignature.verify(responseBody, signature, serverPublicKeys);",
        },
      ],
    },
    {
      title: "Advanced: Public Key Management",
      description: "Extract and manage public keys for verification.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Extract public keys from MajikKey",
          language: "typescript",
          code: "// Works on locked or unlocked keys\nconst publicKeys = MajikSignature.publicKeysFromMajikKey(key);\n\n// publicKeys:\n// {\n//   signerId: string,\n//   edPublicKey: Uint8Array,    // 32 bytes\n//   mlDsaPublicKey: Uint8Array  // 2592 bytes\n// }\n\n// Store or transmit public keys for verification\nawait db.publicKeys.upsert({\n  userId: key.fingerprint,\n  edPk: bytesToBase64(publicKeys.edPublicKey),\n  mlDsaPk: bytesToBase64(publicKeys.mlDsaPublicKey),\n});",
        },
      ],
    },
    {
      title: "Advanced: Re-signing and Stripping",
      description: "Re-sign files or strip signatures to recover originals.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Re-sign a file (idempotent)",
          language: "typescript",
          code: "// signFile() strips existing envelope before signing\nconst { blob: resignedFile } = await MajikSignature.signFile(\n  previouslySignedFile,\n  newKey\n);\n\n// The new signature covers the original content bytes,\n// not the previously signed file",
        },
        {
          title: "Strip signature to recover original",
          language: "typescript",
          code: "const originalFile = await MajikSignature.stripFrom(signedFile);\n\n// originalFile contains the exact bytes that were signed\n// (without any embedded signature envelope)",
        },
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Primitives",
        table: {
          headers: ["Primitive", "Library", "Version", "Usage"],
          rows: [
            {
              cells: [
                "Ed25519",
                "@stablelib/ed25519",
                "^2.1.0",
                "Classical signature (64 bytes)",
              ],
            },
            {
              cells: [
                "ML-DSA-87 (FIPS-204)",
                "@noble/post-quantum",
                "^0.6.1",
                "Post-quantum signature (~4595 bytes)",
              ],
            },
            {
              cells: [
                "SHA-256",
                "@stablelib/sha256",
                "^2.0.1",
                "Content, allowlist, and MJKSIG/MJKSMAP content-hash indexing",
              ],
            },
            {
              cells: ["SHA3-512", "@noble/hashes", "(transitive)", "Seal hash"],
            },
          ],
        },
      },
      {
        title: "Envelope & Protocol Versions",
        table: {
          headers: ["Constant", "Value", "Purpose"],
          rows: [
            {
              cells: [
                "MAJIK_SIGNATURE_VERSION",
                "1",
                "Per-signer envelope format version",
              ],
            },
            {
              cells: [
                "MAJIK_ENVELOPE_VERSION",
                "1",
                "MultiSigEnvelope wrapper version",
              ],
            },
            {
              cells: [
                "MAJIK_TIMESTAMP_VERSION",
                "1",
                "MajikTimestamp payload version",
              ],
            },
            {
              cells: [
                "MAJIK_NOTARY_VERSION",
                "1",
                "Chain anchor memo format version",
              ],
            },
            {
              cells: [
                "MAJIK_SIGNATURE_DOMAIN",
                '"majik-signature-v1:"',
                "Domain separator for content signing",
              ],
            },
            {
              cells: [
                "MAJIK_SEAL_DOMAIN",
                '"majik-seal-v1:"',
                "Domain separator for seal hashing",
              ],
            },
            {
              cells: [
                "MAJIK_TSA_DOMAIN",
                '"majikah-tsa-v-1:"',
                "Domain separator for TSA payloads",
              ],
            },
            {
              cells: [
                "MAJIK_NOTARY_MEMO_DOMAIN",
                '"majik-notary-v-1:"',
                "Domain separator for chain anchor memos",
              ],
            },
          ],
        },
      },
      {
        title: "Binary Container Formats",
        table: {
          headers: [
            "Format",
            "Magic Bytes",
            "Header Length",
            "Media Type",
            "Extension",
          ],
          rows: [
            {
              cells: [
                "MJKSIG (detached envelope)",
                '"MJKSIG" (6 bytes)',
                "12 bytes",
                "application/vnd.majikah.mjksig",
                ".mjksig",
              ],
            },
            {
              cells: [
                "MJKSMAP (batch manifest)",
                '"MJKSMAP" (7 bytes)',
                "13 bytes",
                "application/vnd.majikah.mjksmap",
                ".mjksmap",
              ],
            },
          ],
        },
      },
      {
        title: "Signature Sizes",
        table: {
          headers: ["Component", "Size", "Format in JSON"],
          rows: [
            {
              cells: [
                "Ed25519 signature",
                "64 bytes",
                "base64 string (~88 chars)",
              ],
            },
            {
              cells: [
                "ML-DSA-87 signature",
                "~4595 bytes",
                "base64 string (~6127 chars)",
              ],
            },
            {
              cells: [
                "Ed25519 public key",
                "32 bytes",
                "base64 string (~44 chars)",
              ],
            },
            {
              cells: [
                "ML-DSA-87 public key",
                "2592 bytes",
                "base64 string (~3456 chars)",
              ],
            },
            {
              cells: [
                "Content / allowlist hash (SHA-256)",
                "32 bytes",
                "base64 string (44 chars)",
              ],
            },
            {
              cells: [
                "Seal hash (SHA3-512)",
                "64 bytes",
                "hex string (128 chars)",
              ],
            },
            {
              cells: [
                "Full JSON envelope (per signer)",
                "~10 KB",
                "Minified JSON",
              ],
            },
            {
              cells: [
                "Base64 serialized (per signer)",
                "~14 KB",
                "Base64 string",
              ],
            },
          ],
        },
      },
      {
        title: "Error Hierarchy",
        list: [
          "MajikSignatureError — base class; also thrown for general/unexpected failures (e.g. signing a sealed envelope, missing envelope on seal/anchor)",
          "MajikSignatureKeyError — locked key, missing signing keys, or non-issuer attempting an issuer-only action",
          "MajikSignatureVerificationError — unexpected verification failure (distinct from a handled valid: false result), including a failed TSA signature check",
          "MajikSignatureSerializationError — malformed JSON/base64/MJKSIG/MJKSMAP during parsing or encoding, including malformed binary headers",
          "MajikSignatureAllowlistError — a non-listed signer attempts to sign a restricted file",
          "MajikSignatureValidationError — a structural shape check fails: malformed envelope or batch manifest entry, empty/duplicate batch paths, empty allowlist, mismatched seal fields, or invalid expectedOrder input",
        ],
      },
    ],
  },
  compatibility: {
    title: "Compatibility",
    description:
      "@majikah/majik-signature is distributed as an ES module (type: module) with no native bindings. It works isomorphically across Node.js, browsers, Tauri, Deno, and Bun.",
    runtime: [
      {
        name: "Node.js",
        supported: true,
        minVersion: "18",
        notes:
          "File I/O via fs module for signing local files when used outside the browser.",
      },
      {
        name: "Browser",
        supported: true,
        notes:
          "All modern browsers supported. File API and Blob support required for file signing.",
      },
      {
        name: "Electron",
        supported: true,
        notes:
          "Works in both main and renderer process. File signing works with Electron's file dialogs.",
      },
      {
        name: "Tauri",
        supported: true,
        notes:
          "Used in production in the Majik Signature desktop app. Works alongside a Rust backend for auxiliary processing (e.g., audio/ffmpeg pipelines) without requiring native crypto bindings.",
      },
      {
        name: "Cloudflare Workers",
        supported: true,
        notes:
          "Limited file format support (no fs module). Works with Blob/ArrayBuffer.",
      },
      {
        name: "Bun",
        supported: true,
        notes: "Full compatibility. Bun supports file I/O natively.",
      },
      {
        name: "Deno",
        supported: true,
        notes: "Requires --allow-read for local file operations.",
      },
    ],
    typescript: {
      included: true,
      strictMode: true,
      declaration: true,
    },
  },
  bestPractices: {
    title: "Best Practices",
    description:
      "Recommendations for integrating Majik Signature securely into your application.",
    practices: [
      {
        title: "Always verify signerId against a trusted source",
        description:
          "The library proves content was signed by a specific key fingerprint. It does not prove who owns that key in the real world. Maintain the mapping between signerId and real-world identity through your own means.",
        dos: [
          "After verification, check each result's signerId against an expected fingerprint from your user database or contact list",
          "Treat signerId as an opaque identifier — never display it to users as a name without mapping it first",
          "Use MajikKey.fingerprint as the canonical identifier for a signer",
        ],
        donts: [
          "Don't trust extractPublicKeys() without cross-checking signerId",
          "Don't assume signerId alone proves real-world identity",
          "Don't skip the signerId check after verification",
        ],
        category: "Security",
      },
      {
        title: "Treat verifyFile() results as a per-signer array",
        description:
          "A file's envelope can carry more than one signature. Never assume there's exactly one, and never treat the array's presence as equivalent to every entry being valid.",
        dos: [
          "Check .every((r) => r.valid) when you require all signers to be valid",
          "Inspect each VerificationResult's signerId individually against your allowlist of trusted fingerprints",
          "Use getEnvelopeInfo() when you need signing status (issuer, pending, sealed) rather than parsing verifyFile() output for that purpose",
        ],
        donts: [
          "Don't assume verifyFile() returns a single object — it returns an array",
          "Don't assume presence in extractFrom()'s output implies cryptographic validity",
        ],
        category: "Security",
      },
      {
        title: "Check usesUnattestedTimestamp before trusting an order result",
        description:
          "verifyFileOrder() prefers TSA-attested timestamps automatically, but falls back to self-reported ones when no TSA is present. A self-reported timestamp is tamper-evident but not independently attested.",
        dos: [
          "Surface result.usesUnattestedTimestamp in any UI or audit trail that reports order-verification results",
          "Require TSA-attached signatures for workflows where order is a compliance or legal requirement",
          "Treat result.softTieWarnings as informational, not a failure — identical timestamps mean order is genuinely indistinguishable, not violated",
        ],
        donts: [
          "Don't treat a passing non-strict order result as proof no one else signed — use { strict: true } when the complete signer set matters",
          "Don't silently ignore usesUnattestedTimestamp when the order claim needs to hold up to scrutiny",
        ],
        category: "Security",
      },
      {
        title: "Use continueOnError deliberately in batch signing",
        description:
          "signBatchDetached() aborts the whole batch on the first failure by default, since signing is security-sensitive. Only opt into continueOnError when partial success is genuinely acceptable.",
        dos: [
          "Inspect the returned failures array whenever continueOnError is true",
          "Default to continueOnError: false for small batches where any failure should be investigated immediately",
          "Use continueOnError: true for large batches where a handful of unreadable files shouldn't block the rest",
        ],
        donts: [
          "Don't set continueOnError: true and then ignore the failures array — that silently drops files from the manifest",
        ],
        category: "Reliability",
      },
      {
        title: "Lock keys immediately after signing",
        description:
          "Signing requires an unlocked MajikKey. Lock the key as soon as signing is complete to minimize private key exposure time.",
        dos: [
          "Call key.lock() immediately after sign() or signFile()",
          "Keep the unlock window as short as possible",
          "Consider unlocking only within the scope of a single function",
        ],
        donts: [
          "Don't keep a MajikKey in the unlocked state indefinitely",
          "Don't store the passphrase in memory alongside the key",
        ],
        category: "Security",
      },
      {
        title: "Use signFile() for media and documents",
        description:
          "When signing files, prefer signFile() over sign(bytes) + embedIn(). It handles stripping, signing, and embedding in a single call and is always idempotent. Use signFileDetached() when the envelope needs to live and travel separately from the file.",
        dos: [
          "Use signFile() for PDFs, images, audio, video, and documents",
          "Re-sign files with signFile() — it strips existing envelopes automatically",
          "Let the library auto-detect file formats via magic bytes",
          "Use signFileDetached() for pipelines where the file and its signature are stored or transported independently",
        ],
        donts: [
          "Don't manually strip signatures before signing — signFile() does this for you",
          "Don't sign the same file bytes multiple times outside signFile() and stack signatures",
        ],
        category: "Performance",
      },
      {
        title: "Use canSign() before attempting a restricted signature",
        description:
          "Restricted files reject non-listed signers with a thrown error. Check permission first to give users a clear reason instead of catching an exception.",
        dos: [
          "Call canSign() to surface a human-readable reason before attempting to sign a potentially restricted file",
          "Catch MajikSignatureAllowlistError as a fallback for cases you didn't pre-check",
        ],
        donts: [
          "Don't assume open signing — always account for the possibility of an allowlist",
        ],
        category: "Security",
      },
      {
        title: "Confirm chain transactions before registering an anchor",
        description:
          "registerChainAnchor() trusts the MajikChainAnchor object you provide and does not itself verify on-chain state.",
        dos: [
          "Only call registerChainAnchor() after your chain-integration layer has confirmed the transaction",
          "Verify canAnchor() returns permitted: true (envelope must be sealed) before attempting to build a memo",
        ],
        donts: [
          "Don't register an anchor speculatively before the transaction is confirmed",
        ],
        category: "Security",
      },
      {
        title: "Ensure byte-for-byte content consistency",
        description:
          "The same bytes must be passed to sign() and verify(). For strings, both sides must use UTF-8. For JSON, both sides must use the same stringify output.",
        dos: [
          "Always JSON.stringify() before signing or verifying JSON payloads",
          "Agree on a canonical stringify format (e.g., no whitespace, sorted keys)",
          "For files, preserve the exact bytes — no transcoding, compression, or re-encoding between signing and verification",
        ],
        donts: [
          "Don't sign a JSON object directly — always stringify first",
          "Don't compress or transcode files between signing and verification",
          "Don't assume string encoding is consistent across environments",
        ],
        category: "Security",
      },
      {
        title: "Use CONTENT_TYPES constants for standard labels",
        description:
          "The library exports a CONTENT_TYPES object with standard MIME type strings. Use these for consistency.",
        dos: [
          "Use CONTENT_TYPES.PDF for PDFs, CONTENT_TYPES.JSON for JSON, etc.",
          "Treat contentType as advisory metadata — it is not cryptographically enforced",
        ],
        donts: [
          "Don't rely on contentType for security decisions — it is advisory only",
          "Don't use contentType as a file format hint for embedding — the library auto-detects",
        ],
        category: "Maintainability",
      },
      {
        title: "Use isSigned() as a fast guard before verification",
        description:
          "Checking if a file is signed is much faster than full verification. Use it to skip verification on unsigned files in hot paths.",
        dos: [
          "Call isSigned() first in upload workflows or batch processing",
          "Skip verification entirely if isSigned() returns false",
        ],
        donts: [
          "Don't use isSigned() as a security check — it only checks presence, not validity",
        ],
        category: "Performance",
      },
    ],
  },
  troubleshooting: {
    title: "Troubleshooting",
    commonIssues: [
      {
        problem:
          "sign() throws 'MajikKey is locked. Call unlock() before signing.'",
        symptoms: [
          "MajikSignatureKeyError thrown from sign() or signFile()",
          "key.isLocked is true",
        ],
        causes: [
          "The key was loaded from JSON and is locked by default",
          "The key was locked via key.lock() before signing",
        ],
        solutions: [
          "Call await key.unlock(passphrase) before signing",
          "Check key.isUnlocked before calling sign()",
          "After creating a key with MajikKey.create(), it starts unlocked — no unlock() needed",
        ],
      },
      {
        problem: "sign() throws 'MajikKey has no signing keys.'",
        symptoms: [
          "MajikSignatureKeyError thrown from sign() or signFile()",
          "key.hasSigningKeys is false",
        ],
        causes: [
          "The key was imported via MajikKey.fromJSON() on a legacy account without Ed25519 or ML-DSA keys",
          "The key was created with an old version of @majikah/majik-key",
        ],
        solutions: [
          "Re-import the key via MajikKey.importFromMnemonicBackup() to add signing keys",
          "Check key.hasSigningKeys before attempting to sign",
          "Upgrade the key to the latest format",
        ],
      },
      {
        problem: "signFile() throws MajikSignatureAllowlistError",
        symptoms: [
          "signFile() rejects immediately, before any cryptographic operation",
        ],
        causes: [
          "The file has a signing allowlist established by an earlier signer",
          "The signing key's fingerprint and/or public keys don't exactly match an allowlist entry",
        ],
        solutions: [
          "Call MajikSignature.canSign(file, key) first to check permission and get a reason",
          "Confirm the correct key is being used — all three fields (signerId, edPublicKey, mlDsaPublicKey) must match an allowlist entry",
          "Confirm with the issuer whether this signer was actually included in expectedSigners",
        ],
      },
      {
        problem: "seal() throws when attempting to seal a file",
        symptoms: ["MajikSignatureKeyError thrown from seal()"],
        causes: [
          "The signing key is not the issuer (the signer who established the allowlist)",
          "The envelope is already sealed",
        ],
        solutions: [
          "Only the original issuer's key can seal — verify with getIssuer(file)",
          "Check isSealed(file) before attempting to seal again",
        ],
      },
      {
        problem: "verify() returns valid: false with a reason",
        symptoms: ["result.valid is false", "result.reason is populated"],
        causes: [
          "Content hash doesn't match — the content was modified after signing",
          "One or both signatures failed cryptographic verification",
          "Wrong public keys provided",
        ],
        solutions: [
          "Read result.reason for the specific cause",
          "Verify you're passing the exact same content that was signed",
          "For strings, ensure both sides use UTF-8 encoding",
          "For JSON, ensure both sides use the same JSON.stringify() output",
          "Check publicKeys.signerId matches signature.signerId",
        ],
      },
      {
        problem:
          "verifyFile() returns an unexpected array shape / code assumes a single object",
        symptoms: [
          "TypeScript error or runtime undefined when reading result.valid directly",
        ],
        causes: [
          "verifyFile() returns VerificationResult[] — one entry per signer — not a single VerificationResult",
        ],
        solutions: [
          "Use results.every((r) => r.valid) for an all-valid check",
          "Use results.find((r) => r.signerId === expectedId) to check a specific signer",
          "Legacy single-signature files still return a one-item array — results[0] works for those, but don't assume length === 1 in general",
        ],
      },
      {
        problem: "verifyFile() returns valid: false on a file that was signed",
        symptoms: [
          "File was signed with signFile()",
          "verifyFile() returns one or more entries with valid: false",
        ],
        causes: [
          "File was transcoded, re-encoded, or re-muxed after signing",
          "File format doesn't support durable metadata (Tier 2 trailer was stripped)",
          "File was signed with a different key than the one being verified against",
        ],
        solutions: [
          "Ensure the file has not been re-encoded between signing and verification",
          "Check the returned handler — if it's 'Fallback (Universal Trailer)', the signature may not survive re-muxing",
          "Verify the file is signed: await MajikSignature.isSigned(file) before calling verifyFile()",
          "Extract the signatures and check signerId matches the expected fingerprint",
        ],
      },
      {
        problem:
          "signFile() uses the Tier 2 fallback for a known format instead of the native handler",
        symptoms: [
          "The returned handler is 'Fallback (Universal Trailer)' for a PDF, PNG, WAV, etc.",
          "File format is not auto-detected correctly",
        ],
        causes: [
          "File magic bytes are missing or corrupted",
          "MIME type is incorrect or absent",
          "File is not a standard conformant instance of the format",
        ],
        solutions: [
          "Pass mimeType explicitly in options: { mimeType: 'application/pdf' }",
          "Check the file is not corrupted or truncated",
          "For custom formats, the Tier 2 fallback is expected — it will work but may not survive re-muxing",
        ],
      },
      {
        problem: "addTSA() throws when attaching a trusted timestamp",
        symptoms: [
          "MajikSignatureError or MajikSignatureVerificationError thrown",
        ],
        causes: [
          "A TSA is already attached to this signature — TSAs cannot be replaced",
          "The digest in the timestamp doesn't match the signature's content hash",
          "The TSA's own Ed25519/ML-DSA-87 signature failed verification",
        ],
        solutions: [
          "Check signature.hasTSA before calling addTSA()",
          "Confirm buildTSARequestPayload() was called on the same signature instance being timestamped",
          "Confirm the TSA server's key and signing process are correct",
        ],
      },
      {
        problem: "verifyFileOrder() reports orderRespected: false unexpectedly",
        symptoms: [
          "result.violations is non-empty even though signers appear to have signed in the right sequence",
        ],
        causes: [
          "One or more compared signatures lack a TSA and fell back to a self-reported, client-clock timestamp that doesn't reflect true wall-clock order",
          "An expected signer's signature is invalid and was excluded from the order comparison, changing which pairs are actually compared",
          "expectedOrder was built with the wrong key (a different key than the one that actually signed) for one of the parties",
        ],
        solutions: [
          "Check result.usesUnattestedTimestamp — if true, the comparison relied on self-reported clocks, which are not independently attested",
          "Check result.invalidSigners — an invalid signature is excluded from ordering, not just failing on its own",
          "Confirm expectedOrder was built from the exact MajikKey / ExpectedSigner that actually produced each signature",
        ],
      },
      {
        problem:
          "MajikSignatureEnvelope.fromMJKSIG() or MajikSignatureMap.fromMJKSMAP() throws MajikSignatureSerializationError",
        symptoms: [
          "'Malformed MJKSIG/MJKSMAP: too short to contain a valid header'",
          "'missing magic bytes'",
          "'declared payload length exceeds buffer'",
        ],
        causes: [
          "The input bytes/Blob are not actually a .mjksig/.mjksmap file (wrong file passed)",
          "The file was truncated during transfer or storage",
          "The file was corrupted, or partially overwritten",
        ],
        solutions: [
          "Use isMJKSIG() / isMJKSMAP() as a cheap magic-byte sniff before attempting a full parse",
          "Confirm the file was written via toMJKSIG()/toMJKSMAP() and not accidentally re-encoded or truncated in transit",
          "If reading from disk/network manually, confirm no partial-read truncation occurred before parsing",
        ],
      },
      {
        problem: "registerChainAnchor() throws — envelope not sealed",
        symptoms: ["MajikSignatureError thrown from registerChainAnchor()"],
        causes: [
          "The file's envelope has not been sealed yet",
          "The anchor's digest does not match the envelope's current seal hash (e.g. the seal was re-applied or the anchor was computed against a stale seal)",
        ],
        solutions: [
          "Call canAnchor(file) first and check permitted before attempting to register",
          "Seal the file via seal() before anchoring",
          "Re-fetch the current sealInfo.sealHash and rebuild the anchor if the seal has changed since the anchor was originally computed",
        ],
      },
      {
        problem:
          "Signature envelope JSON is ~10 KB per signer — is this normal?",
        symptoms: [
          "signature.toJSON() produces a large object",
          "signature.serialize() returns a ~14 KB base64 string",
        ],
        causes: [
          "ML-DSA-87 signature is ~4595 bytes (~6 KB base64)",
          "ML-DSA-87 public key is 2592 bytes (~3.5 KB base64)",
          "This is the inherent cost of post-quantum signatures",
        ],
        solutions: [
          "This is expected and normal — post-quantum signatures are larger than classical",
          "The size is negligible relative to any content being signed (a 500 MB video has a ~10 KB signature)",
          "For sidecar storage, use serialize() (base64) instead of JSON for compactness",
        ],
      },
    ],
    debugging: [
      {
        title: "Inspect signatures without verifying",
        description:
          "Use extractFrom() and each signature's getters to examine the envelope without performing cryptographic verification.",
        code: {
          language: "typescript",
          code: "const signatures = await MajikSignature.extractFrom(file);\nfor (const sig of signatures) {\n  console.log('Signer:', sig.signerId);\n  console.log('Timestamp:', sig.timestamp);\n  console.log('Content hash:', sig.contentHash);\n  console.log('Content type:', sig.contentType);\n  console.log('Has TSA:', sig.hasTSA);\n}",
        },
      },
      {
        title: "Inspect multi-sig / allowlist / seal status in one call",
        description:
          "getEnvelopeInfo() returns the full picture — multi-sig status, seal status, issuer, and signatories — in a single read.",
        code: {
          language: "typescript",
          code: "const info = await MajikSignature.getEnvelopeInfo(file);\nconsole.log(info?.isMultiSig, info?.isSealed, info?.signatureCount);\nconsole.log('Issuer:', info?.issuer?.signerId);\nconsole.log('Pending:', info?.signatories?.pending.map((s) => s.signerId));",
        },
      },
      {
        title: "Inspect the full order-verification breakdown",
        description:
          "SignatureOrderResult carries a per-signer breakdown, not just a pass/fail flag — useful for pinpointing exactly which comparison failed.",
        code: {
          language: "typescript",
          code: "const result = await MajikSignature.verifyFileOrder(file, expectedOrder);\nfor (const s of result.signers) {\n  console.log(s.signerId, s.expectedPosition, s.hasSigned, s.valid, s.timestampSource);\n}\nconsole.log('Violations:', result.violations);\nconsole.log('Soft ties:', result.softTieWarnings);",
        },
      },
      {
        title: "Check if a MajikKey can sign",
        description:
          "Before attempting to sign, check the key's signing capability.",
        code: {
          language: "typescript",
          code: "if (!key.hasSigningKeys) {\n  console.error('Key cannot sign — re-import via importFromMnemonicBackup()');\n}\n\nif (key.isLocked) {\n  await key.unlock(passphrase);\n}\n\n// Now safe to sign\nconst signature = await MajikSignature.sign(content, key);",
        },
      },
    ],
  },
  ecosystem: {
    title: "Ecosystem",
    paragraphs: [
      "@majikah/majik-signature is built on top of @majikah/majik-key and provides the cryptographic signature layer for the Majikah ecosystem — from standalone signing apps to invoicing, messaging, and identity products.",
      "The library uses well-established cryptographic primitives from the StableLib and Noble families, both widely deployed in production security and Web3 applications.",
      "The standalone Majik Signature desktop and web app is built directly on this SDK, adding visual and audio stamping, batch processing, order-aware multi-party workflows, and a guided signing UI on top of the same signing, sealing, and detached/batch signing primitives documented here.",
      "Chain anchoring is intentionally a narrow, composable surface — this SDK only prepares the memo and registers an already-confirmed anchor. Actual blockchain submission and confirmation is handled by a separate, dedicated chain-integration layer within the Majikah ecosystem, keeping this SDK's own dependency surface free of any blockchain client code.",
    ],
    relatedPackages: [
      {
        name: "@majikah/majik-key",
        description:
          "Post-quantum seed phrase identity library. Required peer dependency — provides the MajikKey accounts used for signing.",
        url: "https://www.npmjs.com/package/@majikah/majik-key",
        relationship: "peer",
      },
      {
        name: "@majikah/majik-message",
        description:
          "Post-quantum E2EE messaging SDK. Uses Majik Signature for message authentication and content provenance.",
        url: "https://www.npmjs.com/package/@majikah/majik-message",
        relationship: "complement",
      },
      {
        name: "@majikah/majik-invoice",
        description:
          "Invoice data model for the Majikah ecosystem (used by Majik Buwiz). Invoices are signed and encrypted client-side using Majik Signature and Majik Key before storage or sync.",
        url: "https://www.npmjs.com/package/@majikah/majik-invoice",
        relationship: "complement",
      },
      {
        name: "@majikah/majik-envelope",
        description:
          "Envelope/multi-signature utilities used alongside Majik Signature in signing workflows across the Majikah ecosystem.",
        url: "https://www.npmjs.com/package/@majikah/majik-envelope",
        relationship: "complement",
      },
      {
        name: "@noble/post-quantum",
        description:
          "Pure-JS implementation of NIST post-quantum algorithms including ML-DSA-87. Powers the post-quantum signature algorithm.",
        url: "https://www.npmjs.com/package/@noble/post-quantum",
        relationship: "dependency",
      },
      {
        name: "@stablelib/ed25519",
        description:
          "Ed25519 signature implementation. Provides the classical signature algorithm.",
        url: "https://www.npmjs.com/package/@stablelib/ed25519",
        relationship: "dependency",
      },
      {
        name: "@stablelib/sha256",
        description:
          "SHA-256 hash function. Used for content and allowlist hashing.",
        url: "https://www.npmjs.com/package/@stablelib/sha256",
        relationship: "dependency",
      },
      {
        name: "fflate",
        description:
          "Pure-JS ZIP implementation. Powers deterministic Office/ODF (DOCX/XLSX/PPTX/ODT/ODS/ODP) signature embedding.",
        url: "https://www.npmjs.com/package/fflate",
        relationship: "dependency",
      },
    ],
  },
  support: {
    title: "Support & Resources",
    documentation: [
      {
        label: "GitHub Repository",
        url: "https://github.com/Majikah/majik-signature",
        type: "reference",
      },
      {
        label: "npm Package",
        url: "https://www.npmjs.com/package/@majikah/majik-signature",
        type: "reference",
      },
    ],
    community: [
      {
        platform: "GitHub Issues",
        url: "https://github.com/Majikah/majik-signature/issues",
      },
      {
        platform: "GitHub Sponsors",
        url: "https://github.com/sponsors/jedlsf",
      },
    ],
    issues: {
      url: "https://github.com/Majikah/majik-signature/issues",
    },
  },
  cta: {
    title: "Start Signing with Majik Signature",
    description:
      "Add hybrid post-quantum content signing to your app in minutes. Sign bytes, JSON, or files — embed signatures directly in PDFs, images, audio, video, and documents, or keep envelopes detached and portable. Support multi-party signing, allowlists, sealing, chronological order verification, batch signing, and trusted timestamps out of the box. Verify anywhere, anytime.",
    actions: [
      {
        label: "npm install @majikah/majik-signature",
        url: "https://www.npmjs.com/package/@majikah/majik-signature",
        type: "install",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-signature",
        type: "github",
      },
    ],
    closingStatement:
      "Built by Zelijah for the Majikah ecosystem. Open source under Apache-2.0.",
  },

  isEnabled: true,
};
